import React, { useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'

import ModalButton from 'components/Modal/Button'
import Modal from 'components/Modal'
import Checkbox from 'components/Checkbox'
import TextInput from 'components/TextInput'
import DatePicker from 'components/DatePicker'

import { ButtonContainer, screenSize } from 'styles/shared'
import { PremiumFeature } from 'types/PremiumFeature'
import Api from 'services/api'

interface EditPremiumFeatureModalProps {
    isDisplayed: boolean;
    closeCallback: () => void;
    successCallback: (premiumFeature: PremiumFeature) => void;
    premiumFeature: PremiumFeature;
}

const EditPremiumFeatureModal: React.FC<EditPremiumFeatureModalProps> = ({
  isDisplayed,
  closeCallback,
  successCallback,
  premiumFeature
}) => {
  const [formData, setFormData] = useState<PremiumFeature>(premiumFeature)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({ name: '', description: '', upgradeMsg: '' })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleDraftChange = () => {
    const publishedAt = formData.publishedAt ? null : moment().format('YYYY-MM-DDTHH:mm:ss')
    setFormData({
      ...formData,
      publishedAt
    })
  }

  const handelCheckboxChange = (name: string) => {
    setFormData({
      ...formData,
      [name]: !formData[name]
    })
  }

  const onChangeTrialEndDate = (trialEndDate: string) => {
    setFormData({
      ...formData,
      trialEndDate
    })
  }

  const handleSave = async () => {
    try {
      setErrors({ name: '', description: '', upgradeMsg: '' })
      if (isLoading) return
      if (!formData['name'] || !formData['description'] || !formData['upgradeMsg']) {
        if (!formData['name']) { setErrors((prev) => ({ ...prev, name: 'Name is required' })) }
        if (!formData['description']) { setErrors((prev) => ({ ...prev, 'description': 'Description is required' })) }
        if (!formData['upgradeMsg']) { setErrors((prev) => ({ ...prev, 'upgradeMsg': 'Upgrade message is required' })) }
        return
      }
      setIsLoading(true)
      await Api.PremiumFeatures.update(formData)
      successCallback(formData)
    } catch {

    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal title={'Edit Premium Feature'} variant="sm" {...{ closeCallback, isDisplayed }}>
      <S.InputWrapper>
        <Checkbox
          label="Draft"
          checked={formData.publishedAt === null}
          onChange={handleDraftChange}
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <TextInput
        isRequired
        label="Name"
        name="name"
        data-testid={'premium-feature-name'}
        value={formData.name}
        onChange={handleInputChange}
        errorMessage={errors.name}
        disabled
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <TextInput
        isRequired
        label="Description"
        name="description"
        data-testid={'premium-feature-description'}
        value={formData.description}
        onChange={handleInputChange}
        errorMessage={errors.description}
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <TextInput
          isRequired
          label="Upgrade Message"
          name="upgradeMsg"
          data-testid={'premium-feature-upgrade-msg'}
          value={formData.upgradeMsg}
          onChange={handleInputChange}
          errorMessage={errors.upgradeMsg}
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <Checkbox
        label="Accessible By Diagnostics Admin"
        checked={formData.accessibleByAdmin}
        onChange={() => handelCheckboxChange('accessibleByAdmin')}
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <Checkbox
        label="Accessible By FSR"
        checked={formData.accessibleByFsr}
        onChange={() => handelCheckboxChange('accessibleByFsr')}
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <Checkbox
        label="Accessible By Dealers and Techs in Free Trial"
        checked={formData.accessibleInFreeTrial}
        onChange={() => handelCheckboxChange('accessibleInFreeTrial')}
        />
      </S.InputWrapper>
      { formData.accessibleInFreeTrial && (
        <>
          <span>Free Trial End Date</span>
          <S.DatePickerWrapper>
            <S.DatePicker
            label="Date to end Free Trial"
            value={formData.trialEndDate || ''}
            onChangeCallback={(value) => onChangeTrialEndDate(value)}
            />
          </S.DatePickerWrapper>
        </>
      )}
      <ButtonContainer noBorder>
        <ModalButton decoration="secondary" onPress={closeCallback} text="Cancel" />
        <ModalButton decoration="positive" onPress={handleSave} text="Save" />
      </ButtonContainer>
    </Modal>
  )
}

const S = {
  InputWrapper: styled.div`
    margin-bottom: 20px;
    @media ${screenSize.small} {
      margin-bottom: 10px;
    }`,
  DatePickerWrapper: styled.div`
    display: block;
    margin-bottom: 15px;
    & > div{
      width: 100%;
      & > label{
        display: none;
      }
      & div > input {
        padding: 10.5px 14px;
      }
      & > div{
        border-radius: 0px !important;
     }
    }
  `,
  DatePicker: styled(DatePicker)`
    width: 100%;
  `
}

export default EditPremiumFeatureModal
